import { useState } from "react";
import { useParams } from "react-router";
import {
  useCourseProgressMutation,
  useGetCourseLecturesQuery,
  useGetCourseProgressQuery,
  useGetCourseQuizQuery,
  useGetQuizResultQuery,
} from "../../../../app/features/courses/courses.api";
import useError from "../../../../hooks/useError";
import { toast } from "../../../../lib/toast";
import ContentWrapper from "./ContentWrapper";
import EmbedContent from "./EmbedContent";
import PdfContent from "./PdfContent";
import QuizContent from "./QuizContent";
import QuizContentLoading from "./QuizContentLoading";
import TextContent from "./TextContent";
import TextContentLoading from "./TextContentLoading";
import VideoContent from "./VideoContent";
import VideoContentLoading from "./VideoContentLoading";

type Props = {
  currentSectionId?: number;
  currentContentId?: string;
  currentContentType?: "video" | "article" | "quiz" | "pdf" | "embed";
  onContentChange?: (
    sectionId: number,
    contentId: string,
    contentType: "video" | "article" | "quiz" | "pdf" | "embed",
  ) => void;
  seIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  onNext?: () => void;
  onPrevious?: () => void;
  isCurriculumLoading?: boolean;
  progress?: {
    completedLectures: number;
    totalLectures: number;
    percent: number;
  };
  isLastStep?: boolean;
  sectionOrder?: number;
  lectureOrder?: number;
};

export default function UserCourseContent({
  currentContentId,
  currentContentType,
  onNext,
  onPrevious,
  isCurriculumLoading,
  progress,
  isLastStep,
  sectionOrder,
  lectureOrder,
}: Props) {
  const { id: courseId } = useParams();
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());

  // Fetch Lecture Details
  const {
    data: lectureData,
    isLoading: lectureLoading,
    isFetching: lectureFetching,
    error: lectureError,
  } = useGetCourseLecturesQuery(
    {
      cId: courseId!,
      lId: currentContentId!,
    },
    {
      skip: !courseId || !currentContentId || currentContentType === "quiz",
    },
  );

  // Fetch Quiz Details
  const {
    data: quizData,
    isLoading: quizLoading,
    isFetching: quizFetching,
    error: quizError,
  } = useGetCourseQuizQuery(
    {
      cId: courseId!,
      qId: currentContentId!,
    },
    {
      skip: !courseId || !currentContentId || currentContentType !== "quiz",
    },
  );

  // Fetch Quiz Result
  const {
    data: quizResult,
    isLoading: quizResultLoading,
    error: quizResultError,
  } = useGetQuizResultQuery(
    {
      courseId: courseId!,
      quizId: currentContentId!,
    },
    {
      skip: !courseId || !currentContentId || currentContentType !== "quiz",
    },
  );
  useError(quizResultError, "Quiz Result Error");

  // Fetch Course Progress
  const { data: progressData } = useGetCourseProgressQuery(Number(courseId), {
    skip: !courseId,
  });

  const [markComplete, { isLoading: isMarkCompleteLoading }] =
    useCourseProgressMutation();

  // Combine loading states
  const isLoading =
    isCurriculumLoading ||
    lectureLoading ||
    quizLoading ||
    quizResultLoading ||
    lectureFetching ||
    quizFetching;

  const isCompletedItem = currentContentId
    ? progressData?.completedLectureIds?.includes(Number(currentContentId)) ||
      completedItems.has(currentContentId)
    : false;

  const handleComplete = async (isNext?: boolean) => {
    if (currentContentId && !isCompletedItem) {
      try {
        await markComplete({
          courseId: courseId!,
          lectureId: Number(currentContentId),
          lastPositionSec: 0,
          isCompleted: true,
        }).unwrap();
        setCompletedItems((prev) => new Set([...prev, currentContentId]));
        if (!isNext) toast("success", "সফলভাবে সম্পন্ন হয়েছে!");
      } catch (error) {
        toast("error", "Failed to mark complete");
      }
    }
  };

  const handleNextItem = async () => {
    // Mark current as complete before moving next
    try {
      await handleComplete(true);
      if (onNext) onNext();
    } catch (error) {
      toast("error", "Failed to mark complete");
    }
  };

  if (isLoading) {
    switch (currentContentType) {
      case "article":
        return <TextContentLoading />;
      case "video":
        return <VideoContentLoading />;
      case "quiz":
        return <QuizContentLoading />;
      case "pdf":
      case "embed":
        return <VideoContentLoading />; // Reusing video loader for now as they are similar layout
      default:
        return null;
    }
  }

  if (!currentContentId) {
    return (
      <ContentWrapper
        progress={0}
        totalLessons={0}
        completedLessons={progressData?.summary?.totalCompleted || 0}
        isCompleted={false}
        onComplete={() => {}}
        onNext={() => {}}
        onPrevious={() => {}}
        showPrevious={false}
        showNext={false}
      >
        <div className="text-center py-12 text-theme-slate-600">
          কন্টেন্ট পাওয়া যায়নি। অনুগ্রহ করে একটি সেকশন নির্বাচন করুন।
        </div>
      </ContentWrapper>
    );
  }

  if (lectureError || quizError) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm border border-theme-zinc-200 text-center text-red-500">
        কন্টেন্ট লোড করতে সমস্যা হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।
      </div>
    );
  }

  return (
    <ContentWrapper
      progress={progress?.percent || 0}
      totalLessons={progress?.totalLectures || 0}
      completedLessons={progress?.completedLectures || 0}
      isCompleted={isCompletedItem}
      isCompletedLoading={isMarkCompleteLoading}
      isNextDisabled={isMarkCompleteLoading}
      onComplete={handleComplete}
      onNext={handleNextItem}
      onPrevious={onPrevious || (() => {})}
      showPrevious={
        (currentContentType !== "quiz" || quizResult?.completed) && !!onPrevious
      }
      showNext={
        (currentContentType !== "quiz" || quizResult?.completed) && !!onNext
      }
      hideCompleteButton={currentContentType === "quiz"}
      isLastStep={isLastStep}
    >
      {currentContentType === "article" && lectureData && (
        <TextContent
          content={lectureData.articleHtml || ""}
          title={`Section: ${sectionOrder}, Lecture: ${lectureOrder}`}
          loading={isLoading}
        />
      )}

      {currentContentType === "video" && lectureData && (
        <VideoContent
          videoUrl={lectureData.contentUrl || ""}
          title={`Section: ${sectionOrder}, Lecture: ${lectureOrder}`}
          isLoading={isLoading}
        />
      )}

      {currentContentType === "quiz" && quizData && (
        <QuizContent
          questions={
            quizData?.map((q) => ({
              id: q.id,
              question: q.prompt,
              options: q.QuizOption,
            })) || []
          }
          title="কুইজ টেস্ট"
          onComplete={handleComplete}
          courseId={courseId!}
          quizId={currentContentId!}
          isCompleted={isCompletedItem || quizResult?.completed || false}
          quizResult={quizResult}
          onPrevious={onPrevious}
        />
      )}

      {currentContentType === "pdf" && lectureData && (
        <PdfContent
          url={lectureData.contentUrl || ""}
          title={`Section: ${sectionOrder}, Lecture: ${lectureOrder}`}
          loading={isLoading}
        />
      )}

      {currentContentType === "embed" && lectureData && (
        <EmbedContent
          url={lectureData.contentUrl || ""}
          title={`Section: ${sectionOrder}, Lecture: ${lectureOrder}`}
          loading={isLoading}
        />
      )}
    </ContentWrapper>
  );
}
